import React from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  IconButton,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { useServiceAddDialog } from './hooks';
import {
  BasicInfoSection,
  ServiceInfoSection,
  TimingInfoSection,
  RoomInfoSection,
  DialogActionButtons,
} from './components';

interface ServiceAddDialogProps {
  onServiceAdded?: () => void;
}

const ServiceAddDialog: React.FC<ServiceAddDialogProps> = ({ onServiceAdded }) => {
  const {
    open,
    services,
    rooms,
    isEncounterCancelled,
    handleOpen,
    handleClose,
    handleSubmit,
    formData,
    selectedService,
    selectedRoom,
    error,
    handleInputChange,
    handleServiceChange,
    handleRoomChange,
  } = useServiceAddDialog({ onServiceAdded });

  // Don't render the button if the encounter is cancelled
  if (isEncounterCancelled) {
    return null;
  }

  return (
    <>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleOpen}
        sx={{
          backgroundColor: '#162346',
          color: 'white',
          fontWeight: 600,
          px: 3,
          py: 1,
          '&:hover': {
            backgroundColor: '#1a2a52'
          }
        }}
      >
        Add Service
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#ffffff',
          borderBottom: '2px solid #3b82f6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2.5,
          fontWeight: 600,
          color: '#1e293b'
        }}>
          Add New Service
          <IconButton
            onClick={handleClose}
            sx={{ color: '#64748b' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={3}>
            <BasicInfoSection
              priority={formData.priority}
              modifier={formData.modifier}
              onModifierChange={(value) => handleInputChange('modifier', value)}
            />

            <ServiceInfoSection
              services={services}
              selectedService={selectedService}
              serviceCode={formData.serviceCode}
              serviceDescription={formData.serviceDescription}
              description={formData.description}
              onServiceChange={handleServiceChange}
              onDescriptionChange={(value) => handleInputChange('description', value)}
            />

            <TimingInfoSection
              procedureStart={formData.procedureStart}
              procedureDuration={formData.procedureDuration}
              procedureEnd={formData.procedureEnd}
              onStartChange={(value) => handleInputChange('procedureStart', value)}
              onDurationChange={(value) => handleInputChange('procedureDuration', value)}
            />

            <RoomInfoSection
              rooms={rooms}
              selectedRoom={selectedRoom}
              onRoomChange={handleRoomChange}
            />

            {/* Error Message Section */}
            {error && (
              <Alert severity="error">
                {error}
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <DialogActionButtons
            onCancel={handleClose}
            onSubmit={handleSubmit}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ServiceAddDialog;
